import {
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@material-ui/core';
import { Add, ArrowBack } from '@material-ui/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { IProjectUser, ISecurityGroup, IUserSimplified } from '../../api-client/autogenerated';
import { GreenCheck } from '../custom-components/CustomCheckboxes';
import { UserActionType } from '../form-pages/AddOrManageProjectPage';
import { getComparator, stableSort } from '../document-index/DocumentIndexUtils';
import { sortLastNamesAsc, sortLastNamesDesc, updateObjectInStateArray } from '../../scripts/utils';
import pluralize from 'pluralize';
import { useSelector } from 'react-redux';
import { getProjectState } from '../../features/project/selectors';

type AddPreviousUsersTableProps = {
  handleBack: () => void;
  createProjectUsers: (projectUser: IProjectUser[], action?: UserActionType) => void;
  //used to filter out users in the table that are already in the project
  users: IProjectUser[] | undefined;
  simplifiedUsers: IUserSimplified[] | undefined;
  securityGroups: ISecurityGroup[];
  handleUsersAdded: () => void;
};

const headCells = [
  { id: 'company', label: 'Company Name' },
  { id: 'name', label: 'Name' },
  { id: 'email', label: 'Email' },
  { id: 'securityGroupId', label: 'Security Group' },
];

interface PreviousUserRow {
  id: string;
  name: string;
  email: string;
  company: string;
  securityGroupId?: string;
  isChecked: boolean;
  user: IUserSimplified;
}

export default function AddPreviousUsersTable(props: AddPreviousUsersTableProps) {
  const {
    handleBack,
    createProjectUsers,
    users,
    simplifiedUsers,
    securityGroups,
    handleUsersAdded,
  } = props;

  const project = useSelector(getProjectState);

  const [rows, setRows] = useState<PreviousUserRow[]>([]);
  const [orderBy, setOrderBy] = useState('company');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  const [inputCompany, setInputCompany] = useState('');
  const [inputName, setInputName] = useState('');
  const [companyFieldWidth, setCompanyFieldWidth] = useState(0);
  const [nameFieldWidth, setNameFieldWidth] = useState(0);

  const companyColumnRef = useCallback(
    (node: HTMLTableCellElement) => {
      if (node !== null) {
        setCompanyFieldWidth(node.clientWidth);
      }
    },
    [inputCompany, inputName],
  );

  const nameColumnRef = useCallback(
    (node: HTMLTableCellElement) => {
      if (node !== null) {
        setNameFieldWidth(node.clientWidth);
      }
    },
    [inputCompany, inputName],
  );

  const selectedRows = rows.filter((r) => r.isChecked);

  function handleClick(id: string) {
    const index = rows.findIndex((row) => row.id === id);
    setRows((prevState: string | any[]) => [
      ...(prevState?.slice(0, index) || []),
      {
        ...prevState[index],
        isChecked: prevState ? !prevState[index].isChecked : false,
      },
      ...(prevState?.slice(index + 1) || []),
    ]);
  }

  useEffect(() => {
    if (simplifiedUsers) {
      const previousUsers = simplifiedUsers.filter((su) => !users?.some((u) => su.id === u.userId));
      setRows(
        previousUsers.map((user) => ({
          id: user.id,
          name: user.name,
          email: user.email,
          company: user.company?.name || '-',
          securityGroupId: user.securityGroupId,
          isChecked: false,
          user,
        })),
      );
    }
  }, [simplifiedUsers]);

  const handleAddUsersPressed = () => {
    const selectedRows = rows.filter((x) => x.isChecked);
    if (selectedRows.length > 0) {
      const usersToAdd: IProjectUser[] = [];
      selectedRows.forEach((x) => {
        usersToAdd.push({
          user: x.user,
          securityGroupId: x.securityGroupId,
          securityGroup: securityGroups.find((y) => y.id === x.securityGroupId),
        } as IProjectUser);
      });
      createProjectUsers(usersToAdd);
      handleUsersAdded();
    }
  };

  const updateSelectedSecurityGroup = (newSecurityGroupId: string, rowId: string) => {
    const rowToUpdate = rows.find((x) => x.id === rowId);
    if (!rowToUpdate) return;

    const patch: Partial<PreviousUserRow> = { securityGroupId: newSecurityGroupId };
    if (rowToUpdate.securityGroupId !== newSecurityGroupId) {
      patch['isChecked'] = true;
    }

    updateObjectInStateArray(rowId, setRows, patch);
  };

  const createSortHandler = (property: string) => () => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getRef = (id: string) => {
    if (id === 'name') return nameColumnRef;
    if (id === 'company') return companyColumnRef;

    return undefined;
  };

  const filteredRows = rows.filter(
    (row) =>
      (!inputCompany || row.company.toLowerCase().includes(inputCompany.toLowerCase())) &&
      (!inputName || row.name.toLowerCase().includes(inputName.toLowerCase())),
  );

  let dialogContent =
    rows && rows.length > 0 ? (
      <TableContainer style={{ maxHeight: '450px' }}>
        <div
          style={{ display: 'flex', alignItems: 'center', paddingLeft: 63, gap: 16, marginTop: 8 }}
        >
          <TextField
            variant="outlined"
            placeholder="Filter Company"
            value={inputCompany}
            onChange={(e) => setInputCompany(e.target.value)}
            InputProps={{
              style: { height: 32, width: companyFieldWidth - 18 },
            }}
          />
          <TextField
            variant="outlined"
            placeholder="Filter Name"
            value={inputName}
            onChange={(e) => setInputName(e.target.value)}
            InputProps={{
              style: { height: 32, width: nameFieldWidth - 18 },
            }}
          />
        </div>
        <Table aria-label="dense">
          <TableHead>
            <TableCell padding="checkbox">
              <GreenCheck
                checked={filteredRows.length > 0 && filteredRows.every((row) => row.isChecked)}
                indeterminate={
                  filteredRows.some((row) => row.isChecked) &&
                  !filteredRows.every((row) => row.isChecked)
                }
                onChange={(e) => {
                  const filteredIds = new Set(filteredRows.map((row) => row.id));
                  setRows((prevState: PreviousUserRow[]) =>
                    prevState.map((row) => {
                      if (!filteredIds.has(row.id)) return row;
                      return { ...row, isChecked: e.target.checked };
                    }),
                  );
                }}
              />
            </TableCell>
            {headCells.map((h) => (
              <TableCell
                key={h.id}
                ref={getRef(h.id)}
                align="left"
                sortDirection={orderBy === h.id ? order : false}
              >
                <TableSortLabel
                  active={orderBy === h.id}
                  direction={orderBy === h.id ? order : 'asc'}
                  onClick={createSortHandler(h.id)}
                >
                  {h.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableHead>
          <TableBody>
            {stableSort<any>(
              filteredRows,
              orderBy === 'name'
                ? order === 'desc'
                  ? (a: PreviousUserRow, b: PreviousUserRow) => sortLastNamesDesc(a.name, b.name)
                  : (a: PreviousUserRow, b: PreviousUserRow) => sortLastNamesAsc(a.name, b.name)
                : getComparator(order, orderBy),
            ).map((row, index) => {
              return (
                <TableRow hover key={row.id} role="checkbox">
                  <TableCell padding="checkbox">
                    <GreenCheck
                      onClick={() => handleClick(row.id)}
                      checked={row.isChecked}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="left">{row.company}</TableCell>
                  <TableCell align="left">{row.name}</TableCell>
                  <TableCell align="left">{row.email}</TableCell>
                  <TableCell align="left">
                    <FormControl
                      variant="outlined"
                      size="small"
                      style={{ marginTop: 4, marginBottom: 4 }}
                    >
                      <Select
                        id="security-group-dropdown"
                        value={row.securityGroupId}
                        style={{ width: 250 }}
                        onChange={(event) =>
                          updateSelectedSecurityGroup(event.target.value as string, row.id)
                        }
                      >
                        {securityGroups
                          ?.filter(
                            (group) => group.id !== project?.subscriber?.companyViewSecurityGroupId,
                          )
                          .map((group) => {
                            return (
                              <MenuItem key={group.id} value={group.id!}>
                                {group.name!}
                              </MenuItem>
                            );
                          })}
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    ) : (
      <Typography style={{ paddingLeft: 24, paddingTop: 16, paddingBottom: 32 }}>
        No available users from past projects.
      </Typography>
    );

  return (
    <div>
      {!simplifiedUsers ? (
        <div
          style={{ width: '100%', margin: '20px 0px', display: 'flex', justifyContent: 'center' }}
        >
          <CircularProgress size="40px" thickness={3} color="primary" />
        </div>
      ) : (
        dialogContent
      )}
      <div
        style={{
          width: '100%',
          paddingTop: 16,
          paddingBottom: 8,
          paddingLeft: 16,
          paddingRight: 16,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={handleBack}>
          Back
        </Button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Typography style={{ fontWeight: 600 }}>
            {selectedRows.length} {pluralize('User', selectedRows.length)} Selected
          </Typography>
          <Button
            onClick={handleAddUsersPressed}
            variant="contained"
            color="primary"
            disabled={!rows?.find((x) => x.isChecked)}
            style={{
              paddingLeft: 16,
              paddingRight: 16,
            }}
          >
            <Add style={{ paddingRight: 2 }} />
            Add Users
          </Button>
        </div>
      </div>
    </div>
  );
}
